import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Flag } from '@/components/Flag';
import { LANGS, localizePath, storeLangChoice, stripLocale, useLocale } from '@/lib/locale';

// Same page in another language: real links (with hreflang) so crawlers follow them too.
const useLangLinks = () => {
  const { i18n } = useTranslation();
  const current = useLocale();
  const { pathname, search, hash } = useLocation();
  const base = stripLocale(pathname);
  return {
    current,
    hrefFor: (lang) => localizePath(base, lang) + search + hash,
    choose: (lang) => {
      storeLangChoice(lang);
      i18n.changeLanguage(lang);
    },
  };
};

/**
 * Flag dropdown: the current flag opens a menu of flags with language names.
 * Same control in the desktop and mobile header.
 */
export const LanguageSwitcher = ({ className = '' }) => {
  const { t } = useTranslation();
  const { current, hrefFor, choose } = useLangLinks();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`${t('lang.label')}: ${t(`lang.name.${current}`)}`}
        data-testid="lang-switcher"
        className="inline-flex h-11 items-center gap-0.5 rounded-full px-1.5 text-candy-ink transition-colors duration-150 md:px-3 md:hover:bg-candy-surface"
      >
        <Flag lang={current} className="h-[18px] w-[27px]" />
        <span className="ml-1 hidden text-[13px] font-bold md:inline">{t(`lang.short.${current}`)}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-150 max-[359px]:hidden ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <nav
          id={menuId}
          aria-label={t('lang.label')}
          className="absolute right-0 top-full z-50 mt-1 w-48 rounded-2xl bg-candy-surface p-1.5 shadow-[0_16px_40px_-16px_rgb(var(--ink)/0.45)] animate-in fade-in-0 zoom-in-95 duration-150"
        >
          {LANGS.map((lang) => (
            <Link
              key={lang}
              to={hrefFor(lang)}
              hrefLang={lang}
              lang={lang}
              onClick={() => {
                choose(lang);
                setOpen(false);
              }}
              aria-current={lang === current ? 'true' : undefined}
              data-testid={`lang-${lang}`}
              className={`flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-[15px] ${
                lang === current ? 'bg-candy-bg font-bold' : 'font-medium hover:bg-candy-bg'
              }`}
            >
              <Flag lang={lang} />
              {t(`lang.name.${lang}`)}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
};
